import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import PageNavigation from '../../../components/PageNavigation/PageNavigation'
import vps1 from '../../../assets/VPS/vps1.png'
import './VPS.css'

const VPS = () => {
  const [activeStep, setActiveStep] = useState(1)
  const [lightboxImage, setLightboxImage] = useState(null)
  const [lightboxAlt, setLightboxAlt] = useState('')

  const openLightbox = (imageSrc, altText) => {
    setLightboxImage(imageSrc)
    setLightboxAlt(altText)
    document.body.style.overflow = 'hidden'
  }

  const closeLightbox = () => {
    setLightboxImage(null)
    setLightboxAlt('')
    document.body.style.overflow = ''
  }

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && lightboxImage) {
        closeLightbox()
      }
    }
    if (lightboxImage) {
      window.addEventListener('keydown', handleEscape)
    }
    return () => {
      window.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [lightboxImage])

  const steps = [
    {
      id: 1,
      title: 'Create a New VPS',
      description: 'Deploy a virtual private server with your preferred operating system and configuration.',
      image: vps1,
      details: (
        <>
          Navigate to the VPS section and click "New VPS" or "Create VPS" to open the creation interface.
          <br /><br />
          <strong>Select Operating System:</strong> Choose from available templates including Ubuntu, Rocky Linux, Debian, CentOS, and Fedora. Each OS template is pre-configured and ready to use.
          <br />
          <strong>Select Size:</strong> Choose between Preset sizes (Small, Medium, Large, X-Large) or Custom resources. Preset sizes offer predefined CPU, RAM, and storage configurations. Custom resources allow you to specify exact CPU cores, RAM (GB), and disk storage (GB).
          <br />
          <strong>Review Cost:</strong> The interface displays the hourly and monthly cost based on your selected configuration. Costs are calculated dynamically and shown in real-time.
          <br /><br />
          Once you've configured your VPS, click "Create VPS" to start the provisioning process.
        </>
      )
    },
    {
      id: 2,
      title: 'Monitor Creation Progress',
      description: 'Track the VPS creation process through multiple provisioning steps.',
      details: (
        <>
          After clicking "Create VPS", a progress modal will appear showing the creation process:
          <br /><br />
          <strong>Step 1 - Server:</strong> Cloning VM from template
          <br />
          <strong>Step 2 - CPU:</strong> Configuring CPU and memory
          <br />
          <strong>Step 3 - Storage:</strong> Configuring storage
          <br />
          <strong>Step 4 - Network:</strong> Configuring network settings
          <br />
          <strong>Step 5 - Boot:</strong> Starting virtual machine
          <br />
          <strong>Step 6 - IDE:</strong> Installing JupyterLab (if enabled)
          <br /><br />
          You can monitor the progress in real-time or run the creation in the background. Once complete, you'll be able to access your VPS immediately.
        </>
      )
    },
    {
      id: 3,
      title: 'Access Your VPS',
      description: 'Connect to your VPS using SSH, web console, or JupyterLab.',
      details: (
        <>
          Once your VPS is created and running, you can access it through multiple methods:
          <br /><br />
          <strong>SSH Access:</strong> Use the provided IP address and credentials to connect via SSH:
          <pre><code>{`ssh username@your-vps-ip`}</code></pre>
          <br />
          <strong>Web Console:</strong> Click the "Console" button in the VPS detail page to access a browser-based terminal.
          <br />
          <strong>JupyterLab:</strong> If JupyterLab is enabled, click the "JupyterLab" button to open the web-based IDE. This provides a full development environment with file browser, terminal, and notebook support.
          <br /><br />
          Your VPS is fully configured and ready to use. You can install software, deploy applications, or use it for development and testing.
        </>
      )
    },
    {
      id: 4,
      title: 'Manage Your VPS',
      description: 'Control your VPS lifecycle, monitor resources, and manage settings.',
      details: (
        <>
          From the VPS detail page, you can manage your virtual machine:
          <br /><br />
          <strong>Power Controls:</strong> Start, stop, restart, or delete your VPS. Stopped VPS instances are not charged, helping you save costs when not in use.
          <br />
          <strong>Resource Monitoring:</strong> View real-time CPU, memory, and disk usage. Monitor network traffic and connection statistics.
          <br />
          <strong>Settings:</strong> Update VPS configuration, change resource allocation, or modify network settings. Some changes may require a restart.
          <br />
          <strong>Backups:</strong> Create snapshots of your VPS for backup and recovery purposes. Snapshots capture the entire VM state including all data and configuration.
          <br /><br />
          All VPS instances are automatically backed up and can be restored from snapshots if needed.
        </>
      )
    }
  ]

  useEffect(() => {
    const handleScroll = () => {
      const allStepIds = steps.map(s => ({ id: `vps-step-${s.id}`, stepId: s.id }))
      const scrollPosition = window.scrollY + 200

      for (let i = allStepIds.length - 1; i >= 0; i--) {
        const element = document.getElementById(allStepIds[i].id)
        if (element && element.offsetTop <= scrollPosition) {
          setActiveStep(allStepIds[i].stepId)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [steps])

  return (
    <div className="vps-page">
      <div className="vps-content-wrapper">
        <div className="vps-main-content">
          <h1>VPS</h1>
          <p className="intro">
            Deploy and manage virtual private servers (VPS) on Aedify.AI. Create Linux-based VMs 
            with customizable resources, pre-installed development tools, and integrated JupyterLab IDE.
          </p>

          <section className="vps-section">
            <h2>Getting Started with VPS</h2>
            <p className="section-intro">
              Follow these steps to create your first virtual private server and start deploying applications.
            </p>

            {steps.map((step) => (
              <div 
                key={step.id} 
                className={`step-container ${activeStep === step.id ? 'active' : ''}`}
                id={`vps-step-${step.id}`}
              >
                <div className="step-header">
                  <div className="step-number">{step.id}</div>
                  <h3>{step.title}</h3>
                </div>
                <p className="step-description">{step.description}</p>
                {step.image && (
                  <div className="step-image">
                    <img 
                      src={step.image} 
                      alt={`Step ${step.id}: ${step.title}`}
                      onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                      className="clickable-image"
                    />
                  </div>
                )}
                <div className="step-details">
                  {typeof step.details === 'string' ? <p>{step.details}</p> : step.details}
                </div>
              </div>
            ))}
          </section>

          <section>
            <h2>Integrated Development Environment</h2>
            <p>
              Every VPS comes with optional JupyterLab pre-installed, providing a web-based integrated development 
              environment. JupyterLab includes:
            </p>
            <ul>
              <li>File browser and text editor</li>
              <li>Terminal access</li>
              <li>Notebook support for Python, R, Julia, and more</li>
              <li>Extension marketplace</li>
              <li>Git integration</li>
            </ul>
            <p>
              Access JupyterLab directly from your browser without additional configuration. Perfect for data science, 
              machine learning, and development workflows.
            </p>
          </section>

          <PageNavigation />
        </div>

        {/* Right Sidebar - Step Navigation */}
        <aside className="vps-sidebar">
          <div className="sidebar-section">
            <h3>Getting Started</h3>
            <nav className="step-nav">
              {steps.map((step) => (
                <a
                  key={step.id}
                  href={`#vps-step-${step.id}`}
                  className={`step-nav-item ${activeStep === step.id ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveStep(step.id)
                    document.getElementById(`vps-step-${step.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  <span className="step-nav-number">{step.id}</span>
                  <span className="step-nav-title">{step.title}</span>
                </a>
              ))}
            </nav>
          </div>

          <div className="sidebar-section tips-section">
            <h3>Tips</h3>
            <ul className="tips-list">
              <li>Start with a smaller size and scale up as needed</li>
              <li>Use snapshots before making major configuration changes</li>
              <li>Stop VPS instances when not in use to save costs</li>
              <li>Monitor resource usage to optimize performance</li>
              <li>Keep your operating system and software up to date</li>
              <li>Use SSH keys for secure authentication</li>
              <li>Configure firewall rules to secure your VPS</li>
            </ul>
          </div>
        </aside>
      </div>

      {lightboxImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close lightbox">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <img src={lightboxImage} alt={lightboxAlt} className="lightbox-image" />
          </div>
        </div>
      )}
    </div>
  )
}

export default VPS
